import { useState, useEffect } from 'react';
import RakenneosaSelect from '../../RakenneosaSelect';
import TaulukkoFooter from '../../TaulukkoFooter';
import TableLoadingOverlay from './TableLoadingOverlay';
import { VETOLUJUUS_MURTOTAPA_OPTIONS, VETOLUJUUS_LISAHUOMIOT_OPTIONS } from '../../../../../assets/julkisivuRakenneosaOptions';

// Same 768px breakpoint/pattern as PeitepaksuusView.jsx, KuntoarvioWorkspaceV2
// etc. This table is rows-per-sample (unlike Poränäyte, which is
// columns-per-sample) — on a phone the problem is too many FIELD columns
// per row, not too many sample columns, so below 768px each row becomes its
// own card (label-left/value-right) instead of tabbing between samples.
function useIsMobile(breakpoint = 768) {
  const [mobile, setMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return mobile;
}

// Pure view, matching the original VetolujuusBlock.jsx table layout exactly
// (same C/CH/inp style constants, same combined Murtosyvyys cell, same
// RakenneosaSelect combobox for Murtotapa/Lisähuomiot — that component is
// fully generic, no v1 data-model dependency, so it's reused as-is here)
// plus its n/ka/min/max summary line (calcStats there was a trivial
// client-side reduction over the visible rows' own vetolujuus values — pure
// display math, not business logic, so it's kept here as-is).
const MURTOSYVYYS_TYPES = ['up', 'sp', 'yp', 'ap'];

const C  = { border: '1px solid #adb5bd', padding: '4px 5px', fontSize: '0.82rem', verticalAlign: 'middle' };
const CH = { ...C, background: '#04aa00', color: '#fff', fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap' };
const inp = { border: '1px solid #ced4da', borderRadius: 3, padding: '3px 6px', fontSize: '0.9rem', width: '100%', background: '#fff', textAlign: 'center' };

function fmtNum(v, d = 2) {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  return isNaN(n) ? '—' : n.toFixed(d);
}
function calcStats(allRows) {
  const vals = allRows.map(r => parseFloat(r.arvo?.vetolujuus)).filter(n => !isNaN(n) && n > 0);
  if (!vals.length) return null;
  const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
  return { avg, min: Math.min(...vals), max: Math.max(...vals), n: vals.length };
}

// Shared by both the desktop table row and the mobile card below so the 5
// field editors only exist once — each entry is one field's {label, node}.
function fieldEntries(t, a, patchArvo) {
  return [
    { label: 'Ø mm', node: <input style={inp} type="number" defaultValue={a.diameter} onBlur={e => patchArvo(t, 'diameter', e.target.value)} placeholder="55" /> },
    { label: 'Vetolujuus MN/m²', node: <input style={inp} type="number" step="0.1" defaultValue={a.vetolujuus} onBlur={e => patchArvo(t, 'vetolujuus', e.target.value)} placeholder="1.5" /> },
    {
      label: 'Murtosyvyys mm', node: (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <select value={a.murtosyvyysType || 'up'} onChange={e => patchArvo(t, 'murtosyvyysType', e.target.value)} style={inp}>
            {MURTOSYVYYS_TYPES.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <input style={{ ...inp, flex: 1 }} type="number" defaultValue={a.murtosyvyysFrom} onBlur={e => patchArvo(t, 'murtosyvyysFrom', e.target.value)} placeholder="16" />
            <span style={{ color: '#6c757d', flexShrink: 0 }}>–</span>
            <input style={{ ...inp, flex: 1 }} type="number" defaultValue={a.murtosyvyysTo} onBlur={e => patchArvo(t, 'murtosyvyysTo', e.target.value)} placeholder="26" />
          </div>
        </div>
      ),
    },
    { label: 'Murtotapa', node: <RakenneosaSelect options={VETOLUJUUS_MURTOTAPA_OPTIONS} value={a.murtotapa ?? ''} onChange={v => patchArvo(t, 'murtotapa', v)} style={inp} /> },
    { label: 'Lisähuomiot', node: <RakenneosaSelect options={VETOLUJUUS_LISAHUOMIOT_OPTIONS} value={a.lisahuomiot ?? ''} onChange={v => patchArvo(t, 'lisahuomiot', v)} style={inp} placeholder="Valitse tai kirjoita…" /> },
  ];
}

const cardStyle = { border: '1px solid #e2e4e7', borderRadius: 10, padding: '12px 14px', marginBottom: 12, background: '#fff' };
const cardFieldLabelStyle = { fontSize: '0.72rem', color: '#6c757d', flexShrink: 0, width: 130 };
const cardFieldRowStyle = (first) => ({ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderTop: first ? 'none' : '1px solid #f1f3f5' });

export default function VetolujuusView({ eligible, rows, loading, addRow, patchArvo, caption, commitCaption, captionKohdeId, taulukkoNum = 1 }) {
  const allRows = eligible.flatMap(s => rows[s.id] || []);
  const stats = calcStats(allRows);
  const isMobile = useIsMobile();

  return (
    <div style={{ overflowX: 'auto', position: 'relative', minHeight: loading ? 80 : undefined }}>
      <TableLoadingOverlay show={loading} />
      {eligible.length === 0 ? (
        <div style={{ color: '#6c757d', fontSize: '0.82rem' }}>Ei näytteitä joilla on "Veto" valittuna testiksi (Poränäyte-lohko).</div>
      ) : isMobile ? (
        <div>
          {eligible.map(s => {
            const t = (rows[s.id] || [])[0];
            const rakenneosaNimi = s.rakenneosa?.nimi ?? '—';
            if (!t) return (
              <div key={s.id} style={cardStyle}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 6 }}>{rakenneosaNimi} <span style={{ color: '#198754' }}>· {s.nayte_tunnus}</span></div>
                <em style={{ color: '#adb5bd' }}>Ladataan… </em>
                <button type="button" onClick={() => addRow(s)} style={{ border: 'none', background: 'none', color: '#0d6efd', cursor: 'pointer', fontSize: '0.8rem' }}>Yritä uudelleen</button>
              </div>
            );
            const a = t.arvo || {};
            return (
              <div key={t.id} style={cardStyle}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', marginBottom: 8 }}>{rakenneosaNimi} <span style={{ color: '#198754' }}>· {s.nayte_tunnus || '—'}</span></div>
                {fieldEntries(t, a, patchArvo).map((f, i) => (
                  <div key={f.label} style={cardFieldRowStyle(i === 0)}>
                    <div style={cardFieldLabelStyle}>{f.label}</div>
                    <div style={{ flex: 1 }}>{f.node}</div>
                  </div>
                ))}
              </div>
            );
          })}
          {stats && (
            <div className="text-muted" style={{ fontSize: '0.82rem', marginTop: 4 }}>
              n={stats.n} &nbsp;|&nbsp; ka {fmtNum(stats.avg)} MN/m² &nbsp;|&nbsp; min {fmtNum(stats.min)} &nbsp;|&nbsp; max {fmtNum(stats.max)}
            </div>
          )}
        </div>
      ) : (
        <>
          <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '12%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '19%' }} />
              <col style={{ width: '20%' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={CH}>Rakenneosa</th>
                <th style={CH}>Tunnus</th>
                <th style={CH}>Ø mm</th>
                <th style={CH}>Vetolujuus<br />MN/m²</th>
                <th style={CH}>Murtosyvyys mm</th>
                <th style={CH}>Murtotapa</th>
                <th style={CH}>Lisähuomiot</th>
              </tr>
            </thead>
            <tbody>
              {eligible.map(s => {
                // Rows auto-create the moment a sample becomes eligible (see
                // useTestiTypeV2) — this only shows for the brief window
                // before that lands, or if creation failed and needs a retry.
                const t = (rows[s.id] || [])[0];
                if (!t) return (
                  <tr key={s.id}>
                    <td style={{ ...C, textAlign: 'center' }}>{s.rakenneosa?.nimi ?? '—'}</td>
                    <td style={C}>{s.nayte_tunnus}</td>
                    <td style={C} colSpan={5}>
                      <em style={{ color: '#adb5bd' }}>Ladataan… </em>
                      <button type="button" onClick={() => addRow(s)} style={{ border: 'none', background: 'none', color: '#0d6efd', cursor: 'pointer', fontSize: '0.8rem' }}>Yritä uudelleen</button>
                    </td>
                  </tr>
                );
                const a = t.arvo || {};
                return (
                  <tr key={t.id}>
                    <td style={{ ...C, textAlign: 'center', color: '#495057' }}>{s.rakenneosa?.nimi ?? <span style={{ color: '#adb5bd', fontStyle: 'italic' }}>—</span>}</td>
                    <td style={{ ...C, textAlign: 'center' }}>{s.nayte_tunnus || <span style={{ color: '#adb5bd', fontStyle: 'italic' }}>—</span>}</td>
                    {fieldEntries(t, a, patchArvo).map(f => <td key={f.label} style={C}>{f.node}</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {stats && (
            <div className="text-muted" style={{ fontSize: '0.82rem', marginTop: 4 }}>
              n={stats.n} &nbsp;|&nbsp; ka {fmtNum(stats.avg)} MN/m² &nbsp;|&nbsp; min {fmtNum(stats.min)} &nbsp;|&nbsp; max {fmtNum(stats.max)}
            </div>
          )}
        </>
      )}
      {/* kohdeId keyed off captionKohdeId (the already-loaded testi_caption
          row), not tkId — see PeitepaksuusView for why. */}
      <TaulukkoFooter taulukkoNum={taulukkoNum} kohdeId={captionKohdeId ?? 'preview'} caption={caption} onChange={commitCaption} />
    </div>
  );
}
